import PropTypes from 'prop-types';
import * as React from 'react';
import { addUserDetailsToForm, useUser, useUserFields } from '../../hooks/user';
import { DialogInput } from './DialogInput';
import { useForm } from '../../hooks/form';
import { FormContainer } from './FormContainer';

import { Link, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import { EbabAvatar } from '../Avatar';
import { Link as RouterLink } from 'react-router-dom';

export const PROFILE_DIALOG_STATE = Object.freeze({
    Closed: 0,
    OpenMandatory: 1,
    OpenOptional: 2
});

const requiredFields = ['firstName', 'lastName', 'company', 'role'];

const validationConfig = {};

/**
 * Helper function to extract all values from the formState
 * @param {Object} obj - The object to process
 * @returns {Object}
 */
function extractNestedValues(obj) {
    return Object.keys(obj).reduce((acc, key) => {
        acc[key] = obj[key].value;
        return acc;
    }, {});
}

/**
 * Used as replacer function in JSON.stringify to exclude error and
 * clicked attributes when generating string representation of formState
 * @param {String} key - formState key
 * @param {*} value - formState value
 * @returns {undefined|*}
 */
function formStateReplacer(key, value) {
    return ['error', 'clicked'].includes(key) ? undefined : value;
}

/**
 * Renders a dialog for viewing and updating signed-in user's profile details.
 * The dialog can be in one of three states:
 *  - PROFILE_DIALOG_STATE.Closed: Dialog is closed
 *  - PROFILE_DIALOG_STATE.OpenMandatory: Dialog is open and cannot be dismissed without completing profile details.
 *  - PROFILE_DIALOG_STATE.OpenOptional: Dialog is open and can be dismissed
 * @component
 * @param {Number} dialogState - Tracks visibility and behaviour of dialog
 * @param {Function} onFormClose - Function to close this dialog
 * @returns {React.ReactElement} The rendered ProfileDialog component
 */
const ProfileDialog = ({ dialogState, onFormClose }) => {
    const formId = 'profileDialog';

    const { getFirstName, getLastName, getEmail, getCompanyName, getRole } =
        useUserFields();
    const { user, setUser } = useUser();

    const initialFormState = {
        firstName: { value: getFirstName(), error: false, clicked: false },
        lastName: { value: getLastName(), error: false, clicked: false },
        company: { value: getCompanyName(), error: false, clicked: false },
        role: { value: getRole(), error: false, clicked: false }
    };

    const submitConfig = {
        apiUrlSuffix: 'user',
        constructFormPayloadFields: (formState) => {
            const transformedFormState = extractNestedValues(formState);
            return {
                userId: user.auth0Details.sub,
                email: user.auth0Details.email,
                subscriptionType: user.auth0Details.subscription_type,
                ...transformedFormState
            };
        },
        httpMethod: 'put'
    };

    /**
     * Determine whether dialog Submit button should be enabled or disabled.
     *  - If OpenMandatory then only enable if all fields populated
     *  - If OpenOptional then enable if any field is changed, disable again if any field cleared
     * @returns {boolean} - true to enable Submit button
     */
    function enableSubmitButton() {
        if (requiredFields.every((field) => formState[field].value !== '')) {
            return dialogState === PROFILE_DIALOG_STATE.OpenOptional
                ? JSON.stringify(formState, formStateReplacer) !==
                      JSON.stringify(initialFormState, formStateReplacer)
                : true;
        }
        return false;
    }

    const {
        formState,
        setFormField,
        handleSend,
        submissionState,
        resetFields,
        isErrorAndClicked,
        setFormFieldClicked
    } = useForm(
        initialFormState,
        requiredFields,
        validationConfig,
        submitConfig
    );

    /**
     * Submit dialog fields and perform additional processing relating to user attributes:
     *  - set user context to reflect new internal attributes
     *  - add new user attributes to the freshworks widget
     * @returns {Promise<boolean>}
     */
    const persistUserDetails = async () => {
        await handleSend();

        const transformedFormState = extractNestedValues(formState);

        setUser((last) => ({
            ...last,
            internalDetails: {
                ...last.internalDetails,
                ...transformedFormState
            }
        }));

        //update the FreshworksWidget
        addUserDetailsToForm({
            email: user.auth0Details.email,
            firstName: transformedFormState.firstName,
            lastName: transformedFormState.lastName
        });
        return true; //caller expects a bool return for checking validity
    };

    const formFields = (
        <Stack spacing={3} padding={2}>
            <Stack direction='row' spacing={3} alignItems='center'>
                <EbabAvatar />
                <Stack spacing={3} width={'100%'}>
                    <DialogInput
                        id='firstName'
                        label='First name'
                        value={formState.firstName.value}
                        required
                        error={isErrorAndClicked('firstName')}
                        onChange={(e) =>
                            setFormField('firstName', e.target.value)
                        }
                        onBlur={() => setFormFieldClicked('firstName')}
                        data-analyticsid='first-name'
                    />
                    <DialogInput
                        id='lastName'
                        label='Last name'
                        value={formState.lastName.value}
                        required
                        error={isErrorAndClicked('lastName')}
                        onChange={(e) =>
                            setFormField('lastName', e.target.value)
                        }
                        onBlur={() => setFormFieldClicked('lastName')}
                        data-analyticsid='last-name'
                    />
                </Stack>
            </Stack>
            <DialogInput
                id='email'
                label='Email'
                value={getEmail()}
                required
                data-analyticsid='email'
                disabled
            />
            <DialogInput
                id='company'
                label='Company'
                value={formState.company.value}
                required
                error={isErrorAndClicked('company')}
                onChange={(e) => setFormField('company', e.target.value)}
                onBlur={() => setFormFieldClicked('company')}
                data-analyticsid='company'
            />
            <DialogInput
                id='role'
                label='Role'
                value={formState.role.value}
                required
                error={isErrorAndClicked('role')}
                onChange={(e) => setFormField('role', e.target.value)}
                onBlur={() => setFormFieldClicked('role')}
                data-analyticsid='role'
            />
            <Typography
                variant='smallprint'
                textAlign='center'
                sx={{ pl: 2.5, pr: 2.5 }}
            >
                We may contact you about your account at some point in the
                future. You can find out more about this and how we use your
                personal data in our{' '}
                <Link
                    component={RouterLink}
                    to='/privacy-policy'
                    target='_blank'
                    data-analyticsid='privacy-policy'
                >
                    <br />
                    privacy policy
                </Link>
            </Typography>
        </Stack>
    );

    return (
        <FormContainer
            formId={formId}
            formState={formState}
            open={dialogState !== PROFILE_DIALOG_STATE.Closed}
            handleClose={onFormClose}
            title='My Account'
            submissionView='Account details saved'
            requiredFields={requiredFields}
            handleSend={persistUserDetails}
            submissionState={submissionState}
            resetFields={resetFields}
            enableSubmitButton={enableSubmitButton}
            hideCloseButton={dialogState === PROFILE_DIALOG_STATE.OpenMandatory}
            dataAnalyticsId='my-account-form'
            disableEscapeKeyDown={
                dialogState === PROFILE_DIALOG_STATE.OpenMandatory
            }
        >
            {formFields}
        </FormContainer>
    );
};

ProfileDialog.propTypes = {
    dialogState: PropTypes.number.isRequired,
    onFormClose: PropTypes.func.isRequired
};
export default ProfileDialog;
